import type { ReactNode } from 'react'
import React, { createContext, useContext, useMemo } from 'react'
import { useColorMode, type ColorMode } from '../hooks/useColorMode'
import type { Theme } from '@mui/material/styles'

interface ThemeContextType {
    colorMode: ColorMode
    theme: Theme
    setColorMode: (mode: ColorMode) => void
}

// Justified exception: Context export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: context export
export const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

interface ThemeProviderProps {
    children: ReactNode
}

/**
 * Theme context provider.
 * Manages theme mode (system/light/dark) and provides theme object to all children.
 * Uses useColorMode hook internally to handle system preference detection and storage.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const { colorMode, theme, setColorMode } = useColorMode()

    const value = useMemo<ThemeContextType>(
        () => ({
            colorMode,
            theme,
            setColorMode,
        }),
        [colorMode, theme, setColorMode]
    )

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

// Justified exception: Custom hook export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: custom hook export
export const useThemeContext = (): ThemeContextType => {
    const context = useContext(ThemeContext)
    if (context === undefined) {
        throw new Error('useThemeContext must be used within a ThemeProvider')
    }
    return context
}
